import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import deepEqual from 'fast-deep-equal';
import { ReactNode, useEffect } from 'react';
import { FormProvider, Path, useWatch } from 'react-hook-form';
import { SingleValue } from 'react-select';
import {
  AsyncCreatableSelect,
  CreatableSelect,
  DatePicker,
  FormDialog as DetailDialog,
  FormLayout,
  FormRow as Row,
  FormSection as Section,
  Select,
  TextArea,
  TextInput,
  Toggle,
} from '~/components';
import { Wizard } from '~/components/Wizard/Wizard';
import { WizardStepsProps } from '~/components/Wizard/types';
import { COUNTRIES } from '~/constants';
import { StateOrProvinceInput } from '~/features/vault/facility/StateOrProvinceInput';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { useWizardSteps } from '~/hooks/useWizardSteps';
import { Institution } from '~/types';
import { loadInstitutionOptions } from '../api';
import { DEGREE_OPTIONS, emptyEducationForm } from '../constants';
import { educationSchema } from '../schemas';
import { DetailFormValues, EducationFormValues } from '../types';
import { errorHandler, institutionApiToForm } from '../utils';
import { DetailForm } from './DetailForm';
import { EducationalDetailTable } from './DetailTable';

const TAGS = CT.academics.education.educationForm;

const VALID_FIELDS = [
  'startDate',
  'endDate',
  'graduatedFlag',
  'graduationDate',
  'institution',
  'degree',
  'fieldOfStudy',
  'details',
  'explanationTx',
  'institution.name',
  'institution.addressLine1',
  'institution.city',
  'institution.state',
  'institution.country',
  'institution.postalCode',
] as Path<EducationFormValues>[];
const STEP_FIELDS: Path<EducationFormValues>[][] = [
  [
    'institution',
    'institution.name',
    'institution.addressLine1',
    'institution.addressLine2',
    'institution.city',
    'institution.state',
    'institution.postalCode',
    'institution.country',
  ],
  [
    'degree',
    'fieldOfStudy',
    'startDate',
    'endDate',
    'graduatedFlag',
    'graduationDate',
    'explanationTx',
  ],
  ['details'],
];

interface EducationFormProps {
  form: Partial<EducationFormValues>;
  handleClose: () => void;
  handleFormSuccess: (values: EducationFormValues) => void;
  showDetailModal: boolean;
  /**
   * [MCV-1238] Ungated request to open the Education Detail modal. The
   * state machine handles the transition; no validation is performed on
   * `values` here — they are passed through as a courtesy so the machine
   * keeps the form snapshot it already tracks.
   */
  requestDetailOpen: (values: EducationFormValues) => void;
  handleDetailClose: () => void;
  deleteDetail: (id: number) => void;
}

export const EducationForm = (props: EducationFormProps) => {
  const {
    handleClose,
    form,
    handleFormSuccess,
    handleDetailClose,
    requestDetailOpen,
    deleteDetail,
    showDetailModal,
  } = props;
  const methods = useCustomForm<EducationFormValues>({
    resolver: zodResolver(educationSchema),
    defaultValues: form,
  });

  const { control, formState } = methods;

  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });

  useEffect(() => {
    if (formState.isSubmitted || formState.errors.startDate) {
      methods.trigger('startDate');
    }
  }, [endDate]);

  useEffect(() => {
    if (formState.isSubmitted || formState.errors.startDate) {
      methods.trigger('startDate');
    }
  }, [startDate]);

  useEffect(() => {
    /**
     * This is some example logic that will be useful moving forward, so it is explained here for clarity.
     *
     * Because there is auto-populate functionality that happens when a user selects a institution, this useEffect hook is doing the following
     * - (Pre render) formstate.defaultValues are set in the stateMachine.context.educationForm
     * - (First Render) - Form Rendered
     * - User selects an institution, triggering state machine to alter stateMachine.context.educationForm causing this useEffect to fire
     * - formstate.defaultValues is now different than stateMachine.context.educationForm, the form is reset with new stateMachine.context.educationForm
     */

    if (!deepEqual(formState.defaultValues, form)) {
      methods.reset(form);
    }
  }, [form]);

  useServerWarnings(methods, form.warnings, VALID_FIELDS);

  const onSubmitError = (err: unknown) =>
    errorHandler('There was an error submitting the Education form', err);

  const onSubmitSuccess = (values: EducationFormValues) => {
    handleFormSuccess(values);
  };

  /**
   * [MCV-1238] Open the detail modal regardless of parent-form validity.
   * Details are held in the form's local `details` array via
   * `methods.setValue` and only hit the server when the user saves the
   * full education form — so there is no reason to force them to fill out
   * every other field before adding a detail.
   */
  const handleDetailOpen = () => {
    requestDetailOpen(methods.getValues());
  };

  const didGraduate = useWatch({ control, name: 'graduatedFlag' });
  const { currentStep, goBack, goNext, navigationInProgress } = useWizardSteps({
    methods,
    stepFields: STEP_FIELDS,
  });

  const addDetail = (values: DetailFormValues) => {
    handleDetailClose();
    const currentDetails = methods.getValues('details') || [];
    methods.setValue('details', [...currentDetails, values]);
  };
  useEffect(() => {
    const { setValue, getValues } = methods;
    if (!didGraduate) {
      setValue('graduationDate', null);
    } else {
      setValue('explanationTx', null);
      if (getValues('endDate') && !getValues('graduationDate')) {
        setValue('graduationDate', getValues('endDate'));
      }
    }
  }, [didGraduate]);

  const wizardSteps: WizardStepsProps[] = [
    {
      header: 'Institution',
      label: 'Institution',
      cyTag: `${TAGS.form}-institution-step`,
      footer: {
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-institution`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <AsyncCreatableSelect
                name={'institution.name'}
                required
                cyTag={TAGS.institutionNameSelect}
                label={'Institution Name'}
                loadOptions={(input) => loadInstitutionOptions(input)}
                noOptionsMessage="No matching institution — keep typing to add your own"
                // [MCV-1626] The options here are the US Department of
                // Education's institution list, so a provider whose school is
                // missing from it had no way to record their education at all.
                // This was the only entity picker in the vault without the
                // `userAdded` escape hatch that facility, medical board and
                // specialty board already use, and nothing else had to change
                // to support it: educationFormToApi already ships
                // `institutionId: null` for a name whose value is 'userAdded'
                // and persists `institutionName` plus the address fields
                // below, and buildFallbackInstitution reads that same shape
                // back when the record is reopened.
                onCreateOption={(inputValue: string) => {
                  // reset() rather than setValue() for the MCV-1367 reason
                  // described in onChange below -- the nested state/country
                  // Selects do not re-render reliably on a parent-path
                  // setValue, and this clears them for manual entry.
                  methods.reset(
                    {
                      ...methods.getValues(),
                      institution: {
                        ...emptyEducationForm.institution,
                        name: { value: 'userAdded', label: inputValue },
                      },
                    },
                    {
                      keepDirty: true,
                      keepTouched: true,
                      keepErrors: true,
                      keepIsSubmitted: true,
                      keepSubmitCount: true,
                    }
                  );
                }}
                onChange={(
                  option: SingleValue<
                    { value: string; label: string } & { institution: Institution }
                  >
                ) => {
                  // [MCV-1367] Use reset() instead of setValue('institution', ...).
                  // setValue on a parent path updates form state but doesn't reliably
                  // trigger re-renders of nested <Select> Controllers (state/country),
                  // so they appeared blank until the wizard step re-mounted on
                  // back-navigation. reset() re-initializes the form's subscription
                  // model and the nested Selects pick up the new options immediately.
                  // keep* options preserve submission/error state so the date-revalidation
                  // useEffects (which gate on formState.isSubmitted) keep working after
                  // the user picks a new institution mid-flow.
                  const institutionData = option?.institution
                    ? institutionApiToForm(option.institution)
                    : emptyEducationForm.institution;
                  methods.reset(
                    { ...methods.getValues(), institution: institutionData },
                    {
                      keepDirty: true,
                      keepTouched: true,
                      keepErrors: true,
                      keepIsSubmitted: true,
                      keepSubmitCount: true,
                    }
                  );
                }}
              />
            </Row>
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <TextInput
                label={'Address Line 1'}
                required
                name={'institution.addressLine1'}
                cyTag={TAGS.institutionAddressLine1Input}
                autoComplete={'street address address-line1'}
              />
            </Row>
            <Row>
              <TextInput
                label={'Address Line 2'}
                name={'institution.addressLine2'}
                cyTag={TAGS.institutionAddressLine2Input}
                autoComplete={'street address address-line2'}
              />
            </Row>
            <Row>
              <TextInput
                label={'City'}
                required
                name={'institution.city'}
                cyTag={TAGS.institutionCityInput}
                autoComplete={'street address city'}
              />
            </Row>
            <Row>
              <StateOrProvinceInput
                name={'institution.state'}
                countryFieldName={'institution.country'}
                required
                maxProvinceLength={50}
                cyTag={TAGS.institutionStateSelect}
              />
            </Row>
            <Row>
              <TextInput
                label={'Postal Code'}
                required
                name={'institution.postalCode'}
                cyTag={TAGS.institutionPostalCodeInput}
                autoComplete={'postal-code'}
              />
            </Row>
            <Row>
              <Select
                name={'institution.country'}
                label={'Country'}
                required
                cyTag={TAGS.institutionCountrySelect}
                options={COUNTRIES}
              />
            </Row>
            <Row>
              <TextInput name={'fieldOfStudy'} label={'Field of Study'} maxLength={255} />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Degree & Dates',
      label: 'Degree',
      cyTag: `${TAGS.form}-degree-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-degree`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-degree`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <div className="flex flex-col gap-5">
          <Section componentsPerLine={2}>
            <Row>
              <CreatableSelect
                name={'degree'}
                label={'Degree'}
                cyTag={TAGS.degreeSelect}
                options={DEGREE_OPTIONS}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <Toggle
                name={'graduatedFlag'}
                label={'Did you graduate with this degree?'}
                cyTag={TAGS.graduationCheck}
              />
            </Row>
          </Section>
          <Section componentsPerLine={didGraduate ? 3 : 2}>
            <Row>
              <DatePicker
                label={'Start Date'}
                name={'startDate'}
                required
                cyTag={TAGS.startDateInput}
              />
            </Row>
            <Row>
              <DatePicker label={'End Date'} name={'endDate'} required cyTag={TAGS.endDateInput} />
            </Row>
            {didGraduate && (
              <Row>
                <DatePicker
                  required
                  label={'Graduation Date'}
                  name={'graduationDate'}
                  cyTag={TAGS.graduationDateInput}
                />
              </Row>
            )}
          </Section>
          {!didGraduate && (
            <Section componentsPerLine={1}>
              <Row>
                <TextArea
                  name={'explanationTx'}
                  label={'Explanation'}
                  description={'Please explain why you did not graduate'}
                />
              </Row>
            </Section>
          )}
          {footer}
        </div>
      ),
    },
    {
      header: 'Details',
      label: 'Details',
      cyTag: `${TAGS.form}-details-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-details`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Close',
          cyTag: TAGS.closeBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Save',
          cyTag: TAGS.submitBtn,
          isSubmit: true,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <EducationalDetailTable deleteDetail={deleteDetail} handleDetailOpen={handleDetailOpen} />
          {footer}
        </>
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitError)}
        id="education-form"
      >
        <FormLayout className="bg-white text-content-primary">
          <Wizard steps={wizardSteps} currentStep={currentStep} />
        </FormLayout>
        <DetailDialog
          editing={showDetailModal}
          creating={false}
          onClose={handleDetailClose}
          title={'Add Education Detail'}
        >
          <DetailForm addDetail={addDetail} />
        </DetailDialog>
      </form>
    </FormProvider>
  );
};
